/**
 * DEC-1028: transient-failure retry for dashboard data feeds.
 *
 * The dashboard's org-keyed payment feeds (earnings events → the sites-table
 * Earnings chips, earnings figures → the Earnings tile) used to latch a
 * single failed fetch as a permanent error state until full remount — one
 * dev-server restart or network blip and the chips read "—" for the rest of
 * the session. This helper retries the request through short transient
 * windows before the caller commits to its error state.
 *
 * What counts as transient: no HTTP response at all (network drop, server
 * restarting mid-request) or 5xx/429. 4xx are NEVER retried — an auth or
 * validation failure will not heal by asking again (and hammering a 401
 * just obscures the real problem — see the auth-401 triage rule).
 */

/** Backoff schedule between attempts: first retry fast (a restarting dev
 *  server is usually back within a second), second with real breathing room.
 *  length === max retries (so 3 attempts total by default). */
export const TRANSIENT_RETRY_DELAYS_MS = [800, 2400];

export function isTransientRequestError(err) {
  if (!err) return false;
  // Axios shape: no `response` on network-level failures.
  if (!err.response) return true;
  const status = err.response.status;
  return status >= 500 || status === 429;
}

/**
 * Run `fn`, retrying through transient failures on the given backoff
 * schedule. Resolves with fn's value; rethrows the last error once the
 * schedule is exhausted or on the first non-transient error.
 *
 * `sleep` is injectable so tests run without real timers.
 */
export async function withTransientRetry(
  fn,
  { delays = TRANSIENT_RETRY_DELAYS_MS, isTransient = isTransientRequestError, onRetry, sleep } = {}
) {
  const wait = sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= delays.length || !isTransient(err)) throw err;
      if (onRetry) onRetry(err, attempt + 1);
      await wait(delays[attempt]);
      attempt += 1;
    }
  }
}
